/* Copyright (c) 2020-present CloudIO Inc */

import { IoAudit, IoPages, Query } from '@cloudio-saas/datasource-types';
import {
  useStateX,
  useStateXAtomValue,
  useStateXGetter,
  useStateXResetter,
} from '@cloudio-saas/statex';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { usePubSub } from 'common/Context';
import Controller from 'controller/Controller';
import CodeInput from 'custom/CodeInput';
import useComponentSize from 'hooks/useComponentSize';
import Icon, { parseIconText } from 'icons/Icon';
import { IconChooser } from 'icons/IconChooser';
import { PageUpdateEventID } from 'layout/Header/PageUpdateListener';
import { PageView, sidebarActivePageIdAtom } from 'layout/Sidebar/atoms';
import { useControllerCodeEditorTypes } from 'layout/Sidebar/panels/DataSourceView/CodeEditorTypes';
import { selectionAtom } from 'layout/Sidebar/panels/PagesView/PageControllerHistory';
import { kebabCase } from 'lodash-es';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import useUser, { isViewOnlyDeveloper } from 'root/useUser';
import { isFromDB } from 'store/storeTypes';
import { useStoreCurrentRecord } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { useIoPagesEditStore } from 'store/utils/utils';
import { useTheme } from 'theme/Theme';
import WaveDots from 'utils/WaveDots';
import { shrink } from '../../../../utils/isEmpty';
import { IconPulse } from '../../../../utils/Pulse';
import { useAccordianStyles } from '../DataSourceView/DataSourceEdit';
import Checkbox from '../fields/Checkbox';
import TextField from '../fields/TextField';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '0 20px',
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    '& form > *': {
      marginTop: 8,
    },
  },
  editor: {
    flex: 1,
    display: 'block',
  },
  header: {
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
  },
  heading: {
    flex: 1,
  },
  defaultIcon: {},
}));

const sampleCode = `import React from 'react';
import { Box } from '@mui/material';
import type { BaseFunctionProps, FormatColumnValueFunction, IOComponentProps, IconValueWithToolTip } from 'cloudio';
import { getSessionProperty } from 'cloudio';
import platform from 'platform';

function getUserName() {
    return getSessionProperty(platform, 'userName');
}

function PageComponent(props: IOComponentProps) {
  console.log(props, 'PageComponent.render');
  React.useEffect(() => {
      // fetch and initialize page level data
      console.log(props, 'PageComponent.useEffect');
  }, []);
  return null;
}

const NF = {
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }),
};

const formatColumnValue: FormatColumnValueFunction = ({ viewAttribute, value, rawValue, record, theme }) => {
  if (rawValue) {
      let icon: IconValueWithToolTip | undefined;
      switch (rawValue) {
          case 7:
              icon = { iconName: 'check', color: theme.palette.success.main, iconType: 'light' };
              break;
          case 9:
              icon = { iconName: 'wrench', color: theme.palette.warning.main, iconType: 'light' };
              break;
          case 8:
              icon = { iconName: 'times', color: theme.palette.error.main, iconType: 'light' };
              break;
          default:
              icon = { iconName: 'desktop', color: theme.palette.primary.main, iconType: 'light', iconSize: 30, tooltip: 'some tooltip...' + value };
              break;
      }
      return { value, icon };
  }
  return value;
}

function onSomeTextFieldChange({ pageId, itemId, platform, oldValue, value, ...otherProps }: BaseFunctionProps) {
  // validate the user entered value
  if (value === 'test') {
      // show an error message to the user
      platform.showError(\`Invalid value \${value}!\`);
      // return the oldValue to override user entered value
      return oldValue;
  }
  return value;
}

function someDebugFunction(props: BaseFunctionProps) {
  // use this function to just print all the incoming properties
  // console.log(props);
}

async function getPageSX({ theme, platform, appUid }: BaseFunctionProps) {
  // refer https://mui.com/system/the-sx-prop/
  /*
  const darkMode = theme.palette.mode === 'dark';
  return {
      bgcolor: darkMode ? '#000' : '#fff',
      '& #someId': { display: 'none' },
      '& .someCSSClassName': { backgroundColor: theme.palette.background.paper }
  };
  */
  return {};
}

// must export a default object with all the functions to be used by this page
export default {
  formatColumnValue,
  // getPageSX, // page level sx prop refer https://mui.com/system/the-sx-prop/
  onSomeTextFieldChange,
  someDebugFunction,
  // PageComponent, // PageComponent will be rendered at the root of the page and will get unmounted when the page is exited.
}`;

export const editPageFilter: (
  appUid: string,
  pageUid: string,
) => Query<IoPages> = (appUid, pageUid) => ({
  filter: [
    {
      pageUid: { is: pageUid },
    },
    { appUid: { is: appUid } },
  ],
  limit: 1,
});

const PageEdit = ({
  setView,
  appUid,
  pageUid: currentPageId,
}: {
  appUid: string;
  pageUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement => {
  const theme = useTheme();
  const classes = useStyles();
  const store = useIoPagesEditStore(appUid);
  const _id = useStateXAtomValue(sidebarActivePageIdAtom);
  if (_id) {
    // _id will be an empty string during new datasource creation
    store.read({ props: editPageFilter(appUid, _id) });
  }
  const auditStore = useDataStore<IoAudit>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoAudit',
    alias: 'IoAudit-alias',
  });
  const accordianClasses = useAccordianStyles();
  const { record, update } = useStoreCurrentRecord(store);
  const [expanded, setExpanded] = useState(true);
  const [expanded1, setExpanded1] = useState(false);
  const [expanded2, setExpanded2] = useState(false);
  const platform = usePlatform();
  const reset = useStateXResetter();
  const [isPending, setPending] = usePending();
  const s = useComponentSize();
  const get = useStateXGetter();
  const user = useUser();
  const { pageUid } = record ?? {};
  const [_controller, _setController] = useStateX(
    ['db', 'IoPages', pageUid || '_x_', 'controller'],
    '',
  );
  const [controller, setController] = useStateX(
    ['db_edit', 'IoPages', pageUid || '_x_', 'controller'],
    '',
  );
  useLayoutEffect(() => {
    if (!controller && _controller) {
      setController(_controller);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pubSub = usePubSub();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  useEffect(() => {
    return pubSub.sub(
      'restore-page-controller',
      async (e, { controller: ctrl }) => {
        setController(ctrl as string);
        platform.showWarning(
          'Select version restored locally. To restore on the server, you must save the controller after verifying.',
        );
      },
      true,
    );
  }, [pubSub, appUid, setView, platform, setController]);

  useControllerCodeEditorTypes();

  async function savePageController(val: string) {
    if (!pageUid || _controller === val) return;
    const old_lud = get(['db', 'IoPages', pageUid, 'lastUpdateDate']);
    let r = false;
    try {
      r = await platform.postAtom(
        {
          ds: 'IoPages',
          key: pageUid,
          path: [],
          value: null,
          pk: { pageUid },
          extra: { controller: val },
        },
        { appUid, pageUid },
      );
    } catch (e) {
      console.error(e);
    }
    if (r) {
      if (record?.lastUpdateDate === old_lud) {
        update(
          {
            lastUpdateDate: get(['db', 'IoPages', pageUid, 'lastUpdateDate']),
            lastUpdatedBy: get(['db', 'IoPages', pageUid, 'lastUpdatedBy']),
          },
          true,
        );
      }

      await Controller.compile({
        appUid,
        pageId: pageUid,
        platform,
        source: controller,
        fileName: `${pageUid}_PageController.tsx`,
        debug: true,
      });
      // update state after compile. This will trigger a re-render
      // of all the controller components
      _setController(val);

      await platform.sendRequest(
        {
          type: 'PublishEvent',
          event: {
            appUid: 'cloudio',
            ctx: 'PageUpdateListener',
            id: 'all',
            ts: new Date().toISOString(),
            data: {
              appUid,
              pageId: pageUid,
              id: PageUpdateEventID,
              userName: user.userName,
              displayName: user.displayName,
              ts: new Date().toISOString(),
            },
          },
        },
        { appUid, pageUid, roleUid: 'developer' },
      )[0];
    }
  }

  if (!record) {
    return <>Missing current record!</>;
  }

  const { iconName, iconType } = parseIconText(record.pageIcon);

  const fromDB = isFromDB(store.getCurrentRecord());
  return (
    <div className={classes.root}>
      <Accordion
        className={accordianClasses.root}
        expanded={expanded}
        onChange={() => setExpanded((e) => !e)}
      >
        <AccordionSummary
          className={accordianClasses.header}
          expandIcon={<Icon iconName="chevron-down" />}
        >
          <Typography className={accordianClasses.heading}>Details</Typography>
        </AccordionSummary>
        <AccordionDetails className={accordianClasses.details}>
          <form noValidate autoComplete="off">
            <TextField
              disabled={isViewOnlyUser}
              data-testid="pageName"
              autoFocus
              label="Name"
              value={record.pageName ?? ''}
              onChange={(e) =>
                update({
                  pageName: e.target.value,
                  pageUid:
                    !fromDB &&
                    (!record.pageUid ||
                      kebabCase(record.pageName) === record.pageUid)
                      ? shrink(kebabCase(e.target.value))
                      : record.pageUid,
                })
              }
            />
            <TextField
              data-testid="pageUid"
              disabled={fromDB || isViewOnlyUser}
              label="Code"
              value={record.pageUid ?? ''}
              onBlur={() => {
                update({ pageUid: shrink(kebabCase(record.pageUid ?? '')) });
              }}
              onChange={(e) => update({ pageUid: e.target.value })}
              inputProps={{
                maxLength: 40,
              }}
            />
            <FormControl
              data-testid="pageIcon"
              variant="standard"
              required
              fullWidth
              component="fieldset"
            >
              <IconChooser
                disabled={isViewOnlyUser}
                data-testid="pageIcon"
                value={
                  iconName
                    ? { iconName, iconType, color: 'default' }
                    : {
                        iconName: 'desktop',
                        iconType: 'light',
                        color: 'default',
                      }
                }
                onChange={(_value) =>
                  update({ pageIcon: `${_value.iconName}:${_value.iconType}` })
                }
                label="Icon"
              />
            </FormControl>
            <FormControlLabel
              disabled={isViewOnlyUser}
              data-testid="showInNavigationMenu"
              control={
                <Checkbox
                  data-testid="showInNavigationMenu"
                  checked={record.showInNavigationMenu === 'Y'}
                  onChange={(e) =>
                    update({
                      showInNavigationMenu: e.target.checked ? 'Y' : 'N',
                    })
                  }
                />
              }
              label="Show In Navigation Menu"
            />
          </form>
        </AccordionDetails>
      </Accordion>
      {currentPageId === record.pageUid ? (
        <>
          <Accordion
            className={accordianClasses.root}
            expanded={expanded1}
            onChange={() => setExpanded1((e) => !e)}
          >
            <AccordionSummary
              className={accordianClasses.header}
              expandIcon={<Icon iconName="chevron-down" />}
            >
              <Typography className={accordianClasses.heading}>
                Page Metadata
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={accordianClasses.details}>
              <div ref={s.ref} className={classes.editor}>
                <CodeInput
                  disabled={isViewOnlyUser}
                  darkMode={theme.palette.mode !== 'light'}
                  height={900}
                  width={s.width}
                  value={JSON.stringify(record.md, null, '  ')}
                  language="json"
                  allowJSX
                  onChange={(value) => {
                    update({ md: JSON.parse(value) });
                  }}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </>
      ) : null}
      {fromDB && currentPageId === record.pageUid ? (
        <Accordion
          className={accordianClasses.root}
          expanded={expanded2}
          onChange={() => setExpanded2((e) => !e)}
        >
          <AccordionSummary
            className={accordianClasses.header}
            expandIcon={<Icon iconName="chevron-down" />}
          >
            <Typography className={accordianClasses.heading}>
              Page Controller
            </Typography>
            {controller === _controller ? null : (
              <IconButton
                disabled={isViewOnlyUser}
                className={classes.defaultIcon}
                data-tip="Save Controller"
                onClick={(e) => {
                  e.stopPropagation();
                  savePageController(controller);
                }}
              >
                <Icon disabled={isViewOnlyUser} iconName="save" />
                <IconPulse
                  pulseKey="pg_controller_save"
                  pulseMessage="Click here to save the changes made to the controller code"
                />
              </IconButton>
            )}
            {controller === _controller ? null : (
              <IconButton
                disabled={isViewOnlyUser}
                className={classes.defaultIcon}
                data-tip="Reset"
                onClick={(e) => {
                  setController(_controller);
                }}
              >
                <Icon disabled={isViewOnlyUser} iconName="undo" />
              </IconButton>
            )}
            {controller ? null : (
              <IconButton
                disabled={isViewOnlyUser}
                className={classes.defaultIcon}
                data-tip="Populate sample controller code"
                onClick={(e) => {
                  e.stopPropagation();
                  setController(sampleCode);
                }}
              >
                <Icon disabled={isViewOnlyUser} iconName="code" />
                <IconPulse
                  pulseKey="pg_default_code"
                  pulseMessage="Click here to populate a sample controller code"
                />
              </IconButton>
            )}
            {fromDB ? (
              <IconButton
                className={classes.defaultIcon}
                data-tip="View Change History"
                onClick={(e) => {
                  e.stopPropagation();
                  platform.startAsyncTransition(async () => {
                    await auditStore.query({
                      props: {
                        data: {
                          datasourceUid: 'IoPages',
                          attributeCode: 'controller',
                          pkValue: `${record.orgUid}:${record.appUid}:${record.pageUid}`,
                        },
                        filter: [],
                        projection: {
                          auditId: 1,
                          updateDate: 1,
                          updatedBy: 1,
                        },
                        sort: { updateDate: -1 },
                        limit: 2000,
                        force: true,
                      },
                    });
                    reset(selectionAtom);
                    setView('controllerHistory');
                  }, setPending);
                }}
              >
                {isPending ? <WaveDots active /> : <Icon iconName="history" />}
                <IconPulse
                  pulseKey="pg_change_history"
                  pulseMessage="Click here to view change history of the below controller code."
                />
              </IconButton>
            ) : null}
          </AccordionSummary>
          <AccordionDetails className={accordianClasses.details}>
            {fromDB && currentPageId === record.pageUid ? (
              <div ref={s.ref} className={classes.editor}>
                <CodeInput
                  disabled={isViewOnlyUser}
                  darkMode={theme.palette.mode !== 'light'}
                  height={900}
                  width={s.width}
                  value={controller ?? ''}
                  onChange={setController}
                  language="typescript"
                  allowJSX
                  onSave={savePageController}
                />
              </div>
            ) : null}
          </AccordionDetails>
        </Accordion>
      ) : null}
      {fromDB && currentPageId !== record.pageUid ? (
        <>
          <Typography sx={{ m: 2 }}>
            You must navigate to the page, in order to edit this page's
            metadata/controller
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              platform.goToPage(appUid, record.pageUid);
            }}
          >
            Navigate
          </Button>
        </>
      ) : null}
    </div>
  );
};

export default PageEdit;
